import React, { createContext, useState, useEffect, useCallback } from "react";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  // Initialize navigationHistory from sessionStorage or empty array
  const [userData, setUserData] = useState({
    id: null,
    firstName: "N/A",
    lastName: "N/A",
    email: "N/A",
    profilePic: null,
    role: "patient",
    verificationStatus: "Pending",
  });
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState(() => {
    const storedHistory = sessionStorage.getItem("navigationHistory");
    return storedHistory ? JSON.parse(storedHistory) : [];
  });

  const API_ENDPOINT = "/wp-json/dbsb/v1/user/ui-data";
  const USER_DETAILS_ENDPOINT = "/wp-json/dbsb/v1/user/me";
  const NOTIFICATIONS_API_ENDPOINT = "/wp-json/dbsb/v1/notifications/ui";

  // Event listeners for state updates
  const listeners = new Set();

  const subscribe = (callback) => {
    listeners.add(callback);
    return () => listeners.delete(callback);
  };

  const notify = (eventType) => {
    listeners.forEach((callback) => callback(eventType));
  };

  const fetchUserData = useCallback(async () => {
    setIsLoading(true);
    setApiError(null);
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found.");
      }

      const uiResponse = await fetch(API_ENDPOINT, {
        method: "GET",
        credentials: "include",
        headers: { "X-WP-Nonce": window.wpApiSettings.nonce },
      });

      if (!uiResponse.ok) {
        throw new Error("Failed to fetch user UI data");
      }

      const uiData = await uiResponse.json();

      let verificationStatus = "Pending";
      if (uiData.role !== "patient") {
        const userDetailsResponse = await fetch(USER_DETAILS_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: { "X-WP-Nonce": window.wpApiSettings.nonce },
        });

        if (!userDetailsResponse.ok) {
          throw new Error("Failed to fetch user details");
        }

        const userDetailsData = await userDetailsResponse.json();
        verificationStatus =
          mapVerificationStatus(userDetailsData.account_status) || "Pending";
      }

      setUserData({
        id: uiData.id || null, // Store user ID
        firstName: uiData.first_name || "N/A",
        lastName: uiData.last_name || "N/A",
        email: uiData.email || "N/A",
        profilePic: uiData.profile_pic || null,
        role: uiData.role === "author" ? "provider" : "patient",
        verificationStatus,
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
      setApiError(error.message);
      setUserData({
        id: null,
        firstName: "N/A",
        lastName: "N/A",
        email: "N/A",
        profilePic: null,
        role: "patient",
        verificationStatus: "Pending",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchNotificationsCount = useCallback(async () => {
    try {
      if (!window.wpApiSettings?.nonce) {
        return 0;
      }

      const response = await fetch(NOTIFICATIONS_API_ENDPOINT, {
        method: "GET",
        credentials: "include",
        headers: { "X-WP-Nonce": window.wpApiSettings.nonce },
      });

      if (!response.ok) {
        return 0;
      }

      const data = await response.json();
      const count = data.filter((notif) => !notif.read).length;
      setUnreadNotifications(count);
      return count;
    } catch (error) {
      console.error("Error fetching notifications count:", error);
      return 0;
    }
  }, []);

  const updateNavigationHistory = useCallback((path, search) => {
    const normalizedPath = path.replace(/\/+$/, "");
    const fullPath = search ? `${normalizedPath}${search}` : normalizedPath;
    setNavigationHistory((prev) => {
      // Avoid duplicates based on fullPath and keep only the last 3 unique entries
      const newHistory = prev.filter((item) => item.fullPath !== fullPath);
      newHistory.push({ path: normalizedPath, fullPath, search });
      const updatedHistory = newHistory.slice(-3); // Keep only the last 3 pages
      // Persist to sessionStorage
      sessionStorage.setItem(
        "navigationHistory",
        JSON.stringify(updatedHistory)
      );
      return updatedHistory;
    });
  }, []);

  const mapVerificationStatus = (status) => {
    const statusMap = {
      not_approved: "Not approved",
      pending: "Pending",
      approved: "Approved",
      verified: "Verified",
      rejected: "Rejected",
    };
    return statusMap[status.toLowerCase()] || "Pending";
  };

  useEffect(() => {
    // Clear logout flag on app initialization
    sessionStorage.removeItem('dbsb_logout_in_progress');
    
    fetchUserData();
    fetchNotificationsCount();
  }, [fetchUserData, fetchNotificationsCount]);

  return (
    <GlobalContext.Provider
      value={{
        userData,
        setUserData,
        unreadNotifications,
        setUnreadNotifications,
        isLoading,
        apiError,
        fetchUserData,
        fetchNotificationsCount,
        subscribe,
        notify,
        navigationHistory,
        updateNavigationHistory,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
